import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Flag, ArrowSquareOut, EyeSlash, Trash, ArrowsClockwise, CheckCircle,
  MapPin, Buildings, User, CircleNotch,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

const FILTERS = ["open", "resolved", "all"];
const FILTER_LABEL = { open: "À traiter", resolved: "Traités", all: "Tous" };

export default function AdminReview() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [stats, setStats] = useState({ open: 0, resolved: 0, total: 0 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, s] = await Promise.all([
        api.get(`/admin/dm-reports?status=${filter}`),
        api.get("/admin/dm-reports/stats").catch(() => ({ data: null })),
      ]);
      setReports(data);
      if (s.data) setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r, action, note) => {
    await api.post(`/admin/dm-reports/${r.id}/resolve`, { action });
    if (note) toast.success(note);
    load();
  };

  const act = async (r, kind) => {
    setBusy(r.id + kind);
    try {
      if (kind === "hide") {
        await api.post(`/admin/opportunities/${r.signal_id}/decision-makers/${r.contact_id}/hide`);
        await resolve(r, "hidden", "Décideur masqué pour cette opportunité");
      } else if (kind === "delete") {
        if (!window.confirm("Supprimer définitivement ce décideur et le blacklister pour cette entreprise ?")) { setBusy(null); return; }
        await api.delete(`/admin/opportunities/${r.signal_id}/decision-makers/${r.contact_id}`);
        await resolve(r, "blacklisted", "Décideur supprimé et blacklisté");
      } else if (kind === "refresh") {
        await api.post(`/admin/opportunities/${r.signal_id}/decision-makers/refresh`);
        await resolve(r, "relaunched", "Recherche relancée (IA + Prospeo)");
      } else if (kind === "dismiss") {
        await resolve(r, "dismissed", "Signalement ignoré");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-review">
      <div className="overline mb-2">Contrôle qualité IA</div>
      <h1 className="font-display text-3xl font-bold mb-1">Revue IA — décideurs signalés</h1>
      <p className="text-muted text-sm mb-6 max-w-2xl">Les ESN signalent ici les décideurs proposés par l'IA qui semblent erronés. Corrigez une fois : la correction s'applique à toutes les ESN.</p>

      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList className="bg-surface border border-border">
          {FILTERS.map((f) => (
            <TabsTrigger key={f} value={f} data-testid={`review-filter-${f}`}>
              {FILTER_LABEL[f]} <span className="ml-1.5 font-mono text-xs opacity-70">{f === "open" ? stats.open : f === "resolved" ? stats.resolved : stats.total}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : reports.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center text-muted" data-testid="review-empty">
          <Flag size={26} className="mx-auto mb-3 text-accent/60" />
          Aucun signalement dans cette catégorie. 🎉
        </div>
      ) : (
        <div className="space-y-3" data-testid="review-list">
          {reports.map((r) => (
            <div key={r.id} className="surface rounded-lg p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-fg">{r.role_title || "Opportunité"}</span>
                    <span className="flex items-center gap-1 text-xs text-muted"><Buildings size={12} /> {r.company_name || "—"}</span>
                    {r.city && <span className="flex items-center gap-1 text-xs text-muted"><MapPin size={12} /> {r.city}</span>}
                    {r.status === "resolved" && <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded border text-accent border-accent/40 bg-accent/10">{r.resolved_action || "traité"}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-fg/90">
                    <User size={13} className="text-red-400" />
                    <span className="font-medium">{r.contact_name || "Décideur"}</span>
                    {r.contact_title && <span className="text-muted text-xs">· {r.contact_title}</span>}
                    {r.contact_email && <span className="text-muted text-xs font-mono">· {r.contact_email}</span>}
                  </div>
                  {r.reason && <div className="text-xs text-fg/70 italic">« {r.reason} »</div>}
                  <div className="text-[0.7rem] text-muted">Signalé par <span className="text-fg/80">{r.org_name || r.user_email}</span> · {fmtDate(r.at)}</div>
                </div>

                {r.status !== "resolved" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/app/opportunity/${r.signal_id}`} target="_blank" data-testid={`review-open-${r.id}`}
                      className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-md border border-border text-muted hover:text-fg hover:border-accent/40">
                      <ArrowSquareOut size={13} /> Ouvrir
                    </Link>
                    <button disabled={busy} onClick={() => act(r, "hide")} data-testid={`review-hide-${r.id}`}
                      className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-md border border-border text-muted hover:text-amber-400 hover:border-amber-400/40 disabled:opacity-50">
                      <EyeSlash size={13} /> Masquer
                    </button>
                    <button disabled={busy} onClick={() => act(r, "delete")} data-testid={`review-delete-${r.id}`}
                      className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-md border border-border text-muted hover:text-red-400 hover:border-red-400/40 disabled:opacity-50">
                      <Trash size={13} /> Supprimer + blacklister
                    </button>
                    <button disabled={busy} onClick={() => act(r, "refresh")} data-testid={`review-refresh-${r.id}`}
                      className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-md border border-border text-muted hover:text-accent hover:border-accent/40 disabled:opacity-50">
                      {busy === r.id + "refresh" ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />} Relancer
                    </button>
                    <button disabled={busy} onClick={() => act(r, "dismiss")} data-testid={`review-dismiss-${r.id}`}
                      className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-md border border-border text-muted hover:text-fg disabled:opacity-50">
                      <CheckCircle size={13} /> Ignorer
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
